import React from 'react';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import PublicIcon from '@mui/icons-material/Public';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EventIcon from '@mui/icons-material/Event';
import DomainIcon from '@mui/icons-material/Domain';
import { Container, Header, HeaderLeft, BackButton, Title, Content, MainSection } from '../common/styles';
import { StyledCard, StyledIconButton } from '../LandingPage/styles';

const configCards = [
  {
    id: 'countries',
    title: 'Configure Countries',
    description: 'Manage countries and regions',
    icon: <PublicIcon />,
    path: '/organization/countries',
    customIconStyle: { 
      backgroundColor: '#1976D2',
    }
  },
  {
    id: 'business-areas',
    title: 'Configure Business Areas',
    description: 'Manage business areas and domains',
    icon: <CategoryIcon />,
    path: '/organization/business-areas',
    customIconStyle: { 
      backgroundColor: '#388E3C',
    }
  },
  {
    id: 'companies',
    title: 'Configure Companies',
    description: 'Manage company structures',
    icon: <BusinessIcon />,
    path: '/organization/companies',
    customIconStyle: { 
      backgroundColor: '#D32F2F',
    }
  },
  {
    id: 'clients',
    title: 'Configure Clients',
    description: 'Manage client information',
    icon: <GroupsIcon />,
    path: '/organization/clients',
    customIconStyle: { 
      backgroundColor: '#7B1FA2',
    }
  },
  {
    id: 'business-units',
    title: 'Business Units',
    description: 'View business units',
    icon: <AccountTreeIcon />,
    path: '/organization/business-units',
    customIconStyle: { 
      backgroundColor: '#C2185B',
    }
  },
  {
    id: 'clusters',
    title: 'Configure Clusters',
    description: 'Manage organizational clusters',
    icon: <HubIcon />,
    path: '/organization/clusters',
    customIconStyle: { 
      backgroundColor: '#0288D1',
    }
  },
  {
    id: 'positions',
    title: 'Configure Organizational Structure',
    description: 'Manage positions, grades and roles for each Organizational Structure Type',
    icon: <WorkIcon />,
    path: '/organization/positions',
    customIconStyle: { 
      backgroundColor: '#F57C00',
    }
  },
  {
    id: 'contract-types',
    title: 'Configure Client Contract Types',
    description: 'Manage client employment contract types',
    icon: <DescriptionIcon />,
    path: '/organization/contract-types',
    customIconStyle: { 
      backgroundColor: '#455A64',
    }
  },
  {
    id: 'employee-contract-types',
    title: 'Configure Individual Contract Types',
    description: 'Manage individual-specific contract types',
    icon: <BadgeIcon />,
    path: '/organization/employee-contract-types',
    customIconStyle: { 
      backgroundColor: '#00695C',
    }
  },
  {
    id: 'employee-engagement-types',
    title: 'Configure Individual Engagement Types',
    description: 'Manage individual engagement types',
    icon: <WorkHistoryIcon />,
    path: '/organization/employee-engagement-types',
    customIconStyle: {
      backgroundColor: '#5D4037',
    }
  },
  {
    id: 'employee-engagement-models',
    title: 'Configure Individual Engagement Models',
    description: 'Manage individual engagement models',
    icon: <HandshakeIcon />,
    path: '/organization/employee-engagement-models',
    customIconStyle: {
      backgroundColor: '#795548',
    }
  },
  {
    id: 'event-types',
    title: 'Event Types',
    description: 'Manage event types',
    icon: <EventIcon />,
    path: '/organization/event-types',
    customIconStyle: {
      backgroundColor: '#E91E63',
    }
  },
  /*
  {
    id: 'department-types',
    title: 'Department Structure Types',
    description: 'Manage organizational structure types, tied with positions and roles',
    icon: <BusinessIcon />,
    path: '/organization/department-types',
    customIconStyle: {
      backgroundColor: '#00838F',
    }
  },
  */
  {
    id: 'org-unit-types',
    title: 'Organizational Unit Types',
    description: 'Manage organizational unit types with names and descriptions',
    icon: <AccountTreeIcon />,
    path: '/organization/org-unit-types',
    customIconStyle: {
      backgroundColor: '#512DA8',
    }
  },
  {
    // ADR-119: classifies Clusters (not business units — see Organizational Unit Types)
    id: 'org-structure-types',
    title: 'Organizational Structure Types',
    description: 'Classify Clusters (e.g. Global, Vertical, Capability, Region)',
    icon: <DomainIcon />,
    path: '/organization/org-structure-types',
    customIconStyle: {
      backgroundColor: '#00695C',
    }
  },
];

const OrganizationLandingPage = () => {
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Dashboard
          </BackButton>
          <Title>Organization Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection>
          <Typography variant="body1" paragraph>
            Select a configuration option to manage organization settings
          </Typography>
          
          <Grid container spacing={3}>
            {configCards.map((card) => (
              <Grid item xs={12} md={4} key={card.id}>
                <StyledCard onClick={() => navigate(card.path)}>
                  <StyledIconButton
                    sx={{ 
                      ...card.customIconStyle,
                      '&:hover': {
                        ...card.customIconStyle
                      }
                    }}
                  >
                    {card.icon}
                  </StyledIconButton>
                  <Typography variant="h6" align="center">{card.title}</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    {card.description}
                  </Typography>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </MainSection>
      </Content>
    </Container>
  );
};

export default OrganizationLandingPage; 